import {
    Box, Button, Chip, CircularProgress, Collapse, Rating, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl,
    IconButton, InputAdornment, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
    TableRow, TextField, Tooltip, Typography
} from "@mui/material"
import { KeyboardArrowDown, KeyboardArrowUp, Refresh, Search } from "@mui/icons-material"
import { FC, Fragment, useCallback, useEffect, useState } from "react"
import _ from 'lodash'
import PageTitle from "../Common/PageTitle"
import NoData from "../Common/NoData"
import { Apis } from "../../helpers"
import { TABLE_HEIGHT } from "../../configs"
import { EventsEmit } from "../../configs/events"

// Orders placed in the mobile app's Lock Store (collection "lock_orders"). The server only lets the super admin in.
type OrderStatus = 'Awaiting Payment' | 'Pending' | 'Confirmed' | 'Shipped' | 'Delivered' | 'Cancelled'

interface OrderItem {
    lockId: string
    name?: string
    model?: string
    variant?: string
    color?: string
    version?: string
    fingerprint?: boolean
    quantity: number
    unitPriceRmb: number
    unitPriceKes: number
}

interface LockOrder {
    _id: string
    date: string
    status: OrderStatus
    items: OrderItem[]
    rate: number
    totalRmb: number
    totalKes: number
    contact: { name: string, phone: string, location: string, notes?: string }
    user?: { firstName?: string, lastName?: string, userName?: string, email?: string } | null
    // installation is tracked apart from the order's own status: none = waiting for an installer, Installed = the installer says it is
    // done, Completed = the customer confirmed and rated it
    installation?: { status?: 'Pending' | 'Installed' | 'Completed', installedAt?: string, installerName?: string, review?: { rating: number, comment?: string, reviewedAt?: string } }
    payment?: {
        method?: 'Mpesa' | 'Development'
        phone?: string
        amount?: number
        confirmed?: boolean
        paidAt?: string
        failureReason?: string
        refundDue?: boolean
        refIds?: { mpesaTxnId?: string }
    }
}

const STATUSES: OrderStatus[] = ['Awaiting Payment', 'Pending', 'Confirmed', 'Shipped', 'Delivered', 'Cancelled']
// what an order can be moved to by hand ("Awaiting Payment" is settled by M-Pesa, and a cancelled order stays cancelled)
const SETTABLE: OrderStatus[] = ['Pending', 'Confirmed', 'Shipped', 'Delivered', 'Cancelled']
const STATUS_COLOR: Record<OrderStatus, 'default' | 'warning' | 'info' | 'secondary' | 'success' | 'error'> = {
    'Awaiting Payment': 'default', Pending: 'warning', Confirmed: 'info', Shipped: 'secondary', Delivered: 'success', Cancelled: 'error'
}

const ksh = (amount?: number) => `Ksh ${Math.round(amount || 0).toLocaleString('en-US')}`
const formatDateTime = (date?: string) =>
    date ? new Date(date).toLocaleString('en-KE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
const lockCount = (order: LockOrder) => order.items.reduce((sum, item) => sum + item.quantity, 0)
const optionsText = (item: OrderItem) => [item.color, item.version, item.fingerprint ? 'Fingerprint' : null].filter(Boolean).join(' · ')
const toast = (type: 'success' | 'error', message: string) => EventsEmit('modals', { action: 'toastMsg', type, message })

const paymentChip = (order: LockOrder) => {
    const { payment } = order
    if (payment?.refundDue) return <Chip size="small" color="error" label="Refund due" />
    if (payment?.method === 'Development') return <Chip size="small" variant="outlined" label="Development" />
    if (payment?.method === 'Mpesa' && payment.confirmed) return <Chip size="small" color="success" variant="outlined" label="Paid · M-Pesa" />
    if (payment?.method === 'Mpesa') return <Chip size="small" variant="outlined" label={payment.failureReason ? 'Not paid' : 'Awaiting M-Pesa'} />
    return <Typography sx={{ fontSize: 13 }}>—</Typography>
}

// what the Installation column shows: nothing for an order that is not real (unpaid or cancelled), otherwise how far the installation is
const installStage = (order: LockOrder) =>
    order.status === 'Awaiting Payment' || order.status === 'Cancelled' ? null : order.installation?.status === 'Completed' ? 'Completed' : order.installation?.status === 'Installed' ? 'Installed' : 'To install'

interface StateProps {
    orders: LockOrder[]
    loading: boolean
    refreshing: boolean
    status: OrderStatus | 'all'
    searchVal: string
    page: number
    pageSize: number
    count: number
    open: string | null                                   // the order whose items are showing
    updating: string | null                               // the order whose status is being saved
    installing: string | null                             // the order being marked installed
    confirmCancel: LockOrder | null
}

const AdminLockOrders: FC = () => {
    const [state, setState] = useState<StateProps>({
        orders: [], loading: true, refreshing: false, status: 'all', searchVal: '', page: 0, pageSize: 25, count: 0, open: null, updating: null, installing: null, confirmCancel: null
    })
    const { orders, loading, refreshing, status, searchVal, page, pageSize, count, open, updating, installing, confirmCancel } = state

    const fetchOrders = ({ newStatus, newSearch, newPage, newPageSize, processVariable }: any = {}) => {
        const Status = newStatus || state.status
        const Search = newSearch !== undefined ? newSearch : state.searchVal
        const Page = newPage != null && newPage > -1 ? newPage : state.page
        const PageSize = newPageSize || state.pageSize
        if (processVariable) setState(s => ({ ...s, [processVariable]: true }))
        const afterLoad = { loading: false, refreshing: false, status: Status, searchVal: Search, page: Page, pageSize: PageSize }
        Apis({ module: 'Locks', action: 'getLockOrders', status: Status === 'all' ? undefined : Status, search: Search, page: Page, pageSize: PageSize })
            .then((resp: any) => {
                if (resp.error) toast('error', resp.message)
                setState(s => resp.error ? { ...s, ...afterLoad } : { ...s, ...afterLoad, orders: resp.data || [], count: resp.count ?? s.count, open: null })
            })
            .catch(() => setState(s => ({ ...s, ...afterLoad })))
    }

    // eslint-disable-next-line
    const startSearch = useCallback(_.debounce((val: string) => fetchOrders({ newSearch: val, newPage: 0, processVariable: 'loading' }), 500), [])

    useEffect(() => {
        fetchOrders({ processVariable: 'loading' })
        return () => startSearch.cancel()
        // eslint-disable-next-line
    }, [])

    const changeStatus = (order: LockOrder, newStatus: OrderStatus) => {
        if (newStatus === order.status) return
        setState(s => ({ ...s, updating: order._id, confirmCancel: null }))
        Apis({ module: 'Locks', action: 'updateLockOrderStatus', orderId: order._id, status: newStatus })
            .then((resp: any) => {
                if (resp.error) toast('error', resp.message)
                else toast('success', resp.message)
                // keep what the list already knew (the account), take the rest from the saved order
                setState(s => ({ ...s, updating: null, orders: resp.error ? s.orders : s.orders.map(o => o._id === order._id ? { ...o, ...resp.order, user: o.user } : o) }))
            })
            .catch(() => setState(s => ({ ...s, updating: null })))
    }

    // The installer reported it done (e.g. by phone): the customer is asked to confirm it and rate it. Installers normally do this
    // themselves from the app.
    const markInstalled = (order: LockOrder) => {
        setState(s => ({ ...s, installing: order._id }))
        Apis({ module: 'Locks', action: 'markLockOrderInstalled', orderId: order._id })
            .then((resp: any) => {
                toast(resp.error ? 'error' : 'success', resp.message)
                setState(s => ({ ...s, installing: null }))
                fetchOrders()
            })
            .catch(() => setState(s => ({ ...s, installing: null })))
    }

    const askStatus = (order: LockOrder, newStatus: OrderStatus) =>
        newStatus === 'Cancelled' ? setState(s => ({ ...s, confirmCancel: order })) : changeStatus(order, newStatus)

    const headCell = { fontWeight: 'bold', fontSize: 13 }
    const cell = { fontSize: 13 }
    const paidByMpesa = confirmCancel?.payment?.method === 'Mpesa' && confirmCancel.payment.confirmed

    return (
        <Box sx={{ pr: 5, height: '100%' }}>

            <PageTitle title="Lock Orders" />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 3, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    {count > 0 && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{count.toLocaleString()} {count === 1 ? 'order' : 'orders'}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Search orders"
                        defaultValue={searchVal}
                        onChange={e => startSearch(e.target.value)}
                        sx={{ width: 260, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 } }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18 }} /></InputAdornment> }}
                    />
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                        <Select
                            value={status}
                            onChange={e => fetchOrders({ newStatus: e.target.value, newPage: 0, processVariable: 'loading' })}
                            sx={{ fontSize: 13, borderRadius: 2 }}
                        >
                            <MenuItem sx={{ fontSize: 13 }} value="all">All Statuses</MenuItem>
                            {STATUSES.map(s => <MenuItem key={s} sx={{ fontSize: 13 }} value={s}>{s}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchOrders({ processVariable: 'refreshing' })} disabled={refreshing}>
                            {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {!orders.length || loading ?
                <NoData message={searchVal || status !== 'all' ? 'No orders match your search.' : 'No lock orders have been placed yet.'} loading={loading} /> :
                <Paper sx={{ width: '100%' }} elevation={0} variant="outlined">
                    <TableContainer sx={{ maxHeight: TABLE_HEIGHT }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...headCell, width: 40 }} />
                                    <TableCell sx={headCell}>Placed</TableCell>
                                    <TableCell sx={headCell}>Customer</TableCell>
                                    <TableCell sx={headCell}>Locks</TableCell>
                                    <TableCell sx={headCell}>Deliver to</TableCell>
                                    <TableCell sx={headCell} align="right">Total</TableCell>
                                    <TableCell sx={headCell}>Payment</TableCell>
                                    <TableCell sx={headCell}>Installation</TableCell>
                                    <TableCell sx={headCell}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {orders.map(order => {
                                    const isOpen = open === order._id
                                    const locked = order.status === 'Awaiting Payment' || order.status === 'Cancelled'
                                    const account = order.user ? [order.user.firstName, order.user.lastName].filter(Boolean).join(' ') || order.user.userName : null
                                    return (
                                        <Fragment key={order._id}>
                                            <TableRow hover sx={{ '& > td': { borderBottom: isOpen ? 'unset' : undefined } }}>
                                                <TableCell>
                                                    <IconButton size="small" aria-label="Show items" onClick={() => setState(s => ({ ...s, open: isOpen ? null : order._id }))}>
                                                        {isOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell sx={cell}>{formatDateTime(order.date)}</TableCell>
                                                <TableCell sx={cell}>
                                                    <Box sx={{ fontWeight: 600 }}>{order.contact.name}</Box>
                                                    <Box sx={{ color: 'text.secondary' }}>{order.contact.phone}</Box>
                                                    {account && account !== order.contact.name && <Box sx={{ color: 'text.secondary', fontSize: 12 }}>Account: {account}</Box>}
                                                </TableCell>
                                                <TableCell sx={cell}>{lockCount(order)}</TableCell>
                                                <TableCell sx={{ ...cell, maxWidth: 220 }}>{order.contact.location}</TableCell>
                                                <TableCell sx={{ ...cell, fontWeight: 600 }} align="right">{ksh(order.totalKes)}</TableCell>
                                                <TableCell sx={cell}>{paymentChip(order)}</TableCell>
                                                <TableCell sx={cell}>
                                                    {(() => {
                                                        const stage = installStage(order)
                                                        if (!stage) return '—'
                                                        if (stage === 'Completed')
                                                            return (
                                                                <Tooltip title={order.installation?.review?.comment || 'No comment left'}>
                                                                    <Box>
                                                                        <Chip size="small" color="success" label="Completed" />
                                                                        {order.installation?.review && <Rating size="small" readOnly value={order.installation.review.rating} sx={{ display: 'flex', mt: 0.5 }} />}
                                                                    </Box>
                                                                </Tooltip>
                                                            )
                                                        if (stage === 'Installed')
                                                            return (
                                                                <Box>
                                                                    <Chip size="small" color="info" label="Installed" />
                                                                    <Box sx={{ color: 'text.secondary', fontSize: 12, mt: 0.5 }}>Waiting for the customer to confirm</Box>
                                                                </Box>
                                                            )
                                                        return (
                                                            <Button size="small" variant="outlined" disabled={installing === order._id} onClick={() => markInstalled(order)} sx={{ textTransform: 'none', fontSize: 12 }}>
                                                                {installing === order._id ? 'Saving...' : 'Mark installed'}
                                                            </Button>
                                                        )
                                                    })()}
                                                </TableCell>
                                                <TableCell sx={cell}>
                                                    {locked ?
                                                        <Chip size="small" color={STATUS_COLOR[order.status]} label={order.status} /> :
                                                        <Select
                                                            size="small"
                                                            value={order.status}
                                                            disabled={updating === order._id}
                                                            onChange={e => askStatus(order, e.target.value as OrderStatus)}
                                                            sx={{ fontSize: 13, minWidth: 130, borderRadius: 2 }}
                                                            endAdornment={updating === order._id ? <CircularProgress size={16} sx={{ mr: 3 }} /> : undefined}
                                                        >
                                                            {SETTABLE.map(s => <MenuItem key={s} sx={{ fontSize: 13 }} value={s}>{s}</MenuItem>)}
                                                        </Select>}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ py: 0 }} colSpan={9}>
                                                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                        <Box sx={{ my: 1.5, ml: 5, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                            <Table size="small" sx={{ maxWidth: 640, flex: '1 1 480px' }}>
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell sx={headCell}>Lock</TableCell>
                                                                        <TableCell sx={headCell}>Options</TableCell>
                                                                        <TableCell sx={headCell} align="right">Qty</TableCell>
                                                                        <TableCell sx={headCell} align="right">Each</TableCell>
                                                                        <TableCell sx={headCell} align="right">Subtotal</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {order.items.map((item, i) => (
                                                                        <TableRow key={i}>
                                                                            <TableCell sx={cell}>{item.name}{item.model && item.model !== item.name ? <Box component="span" sx={{ color: 'text.secondary' }}> · {item.model}</Box> : null}</TableCell>
                                                                            <TableCell sx={cell}>{optionsText(item) || '—'}</TableCell>
                                                                            <TableCell sx={cell} align="right">{item.quantity}</TableCell>
                                                                            <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }} align="right">{ksh(item.unitPriceKes)}</TableCell>
                                                                            <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }} align="right">{ksh(item.unitPriceKes * item.quantity)}</TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                            <Box sx={{ fontSize: 13, display: 'grid', gap: 0.6, alignContent: 'start', minWidth: 260 }}>
                                                                <Box><b>Supplier cost:</b> ¥{order.totalRmb.toLocaleString('en-US')} at Ksh {order.rate.toFixed(2)} per ¥1</Box>
                                                                {order.contact.notes && <Box><b>Notes:</b> {order.contact.notes}</Box>}
                                                                {order.user?.email && <Box><b>Account:</b> {order.user.userName ? `@${order.user.userName} · ` : ''}{order.user.email}</Box>}
                                                                {order.payment?.method === 'Mpesa' && (
                                                                    <Box>
                                                                        <b>M-Pesa:</b> {order.payment.phone} · {ksh(order.payment.amount)}
                                                                        {order.payment.refIds?.mpesaTxnId ? ` · ${order.payment.refIds.mpesaTxnId}` : ''}
                                                                        {order.payment.paidAt ? ` · paid ${formatDateTime(order.payment.paidAt)}` : ''}
                                                                    </Box>
                                                                )}
                                                                {order.payment?.failureReason && <Box><b>Not paid:</b> {order.payment.failureReason}</Box>}
                                                                {order.payment?.refundDue && <Box sx={{ color: 'error.main', fontWeight: 600 }}>Refund due: {ksh(order.payment.amount)} to {order.payment.phone}</Box>}
                                                                {order.installation?.installerName && (
                                                                    <Box><b>Installed by:</b> {order.installation.installerName} · {formatDateTime(order.installation.installedAt)}</Box>
                                                                )}
                                                                {order.installation?.review && (
                                                                    <Box><b>Customer review:</b> {order.installation.review.rating}/5{order.installation.review.comment ? ` · "${order.installation.review.comment}"` : ''}</Box>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </Fragment>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={count}
                        page={page}
                        onPageChange={(_, newPage) => fetchOrders({ newPage, processVariable: 'loading' })}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={e => fetchOrders({ newPage: 0, newPageSize: parseInt(e.target.value, 10), processVariable: 'loading' })}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            }

            <Dialog open={Boolean(confirmCancel)} onClose={() => setState(s => ({ ...s, confirmCancel: null }))}>
                <DialogTitle>Cancel this order?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: 14 }}>
                        {confirmCancel?.contact.name}'s order of {confirmCancel ? lockCount(confirmCancel) : 0} lock(s) worth {ksh(confirmCancel?.totalKes)} will be cancelled and they will be told.
                        A cancelled order cannot be reopened.
                        {paidByMpesa && ` They paid by M-Pesa, so ${ksh(confirmCancel?.payment?.amount)} will be marked as due back to ${confirmCancel?.payment?.phone}.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setState(s => ({ ...s, confirmCancel: null }))}>Keep order</Button>
                    <Button color="error" variant="contained" disableElevation onClick={() => confirmCancel && changeStatus(confirmCancel, 'Cancelled')}>Cancel order</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default AdminLockOrders
